import _ from 'lodash';
import { Dispatch, AnyAction } from 'redux';
import { routerHelperActions } from '@wfp-common/store/routerHelperSlice';
import { SearchParamsHook } from '@wfp-common/hooks/useSearchParams';

import Action from '../action';
import {
    getManager,
    getMyself,
    getPendingActions,
    login,
    authLogout,
    authRefresh,
    newAuthToken,
    resetPassword,
    sendOtpCode,
    requestPasswordReset as requestPasswordResetApi,
} from '../apiClient';
import Manager from '../managers/manager';
import State from '../app/store/state';
import {
    ActionTypes as SessionProlongActionTypes,
    closeSessionProlongAlert,
    resetSessionProlongAlertState,
} from '../utils/sessionExpiration/sessionExpiration';
import { closeNotificationAction, errorAction, successAction } from '../utils/notifications';
import { ActionTypes as SecurityActionTypes } from '../security/security';
import { AppConfigActionCreators } from '../app/appConfig';
import { ManagerPermission } from '../permission-profiles/permission';
import { windowStorage, WindowStorageToken } from '../windowStorage';

export class AuthState {
    apiToken?: string;
    manager?: Manager;
    error?: unknown;
    resetLinkIsSent?: boolean;
    resetOtpSuccessful?: boolean;

    constructor(
        apiToken: string = null,
        manager: Manager = null,
        error: unknown = null,
        resetLinkIsSent?: boolean,
        resetOtpSuccessful?: boolean
    ) {
        this.apiToken = apiToken || windowStorage.getItem(WindowStorageToken.managerApiToken);
        this.resetLinkIsSent = resetLinkIsSent;
        this.resetOtpSuccessful = resetOtpSuccessful;
        this.manager = manager;
        this.error = error;
    }
}

export function hasFeatureAccess(auth: AuthState, feature: ManagerPermission) {
    if (auth.manager?.profile?.permissions) {
        return auth.manager.profile.permissions.includes(feature);
    }
    return false;
}

export function hasOneOfPermissions(auth: AuthState, features: ManagerPermission[]) {
    if (auth.manager?.profile?.permissions) {
        const commonFeatures = _.intersection(auth.manager.profile.permissions, features);
        return commonFeatures.length > 0;
    }
    return false;
}

export function doesManagerPermissionsIncludeGivenFeatures(features: ManagerPermission[], manager: Manager) {
    return manager && _.intersection(manager.profile?.permissions, features).length === features.length;
}

export const ActionTypes = {
    loginSuccessful: 'LoginPage.login',
    loginFailed: 'LoginPage.loginFailed',
    logoutSuccessful: 'LoginPage.logout',
    selfEdit: 'LoginPage.selfEdit',
    reloadUserSuccess: 'LoginPage.reloadUserSuccess',
    resetOtpRequested: 'ResetOtpPage.request',
    resetOtpSuccessful: 'ResetOtpPage.success',
    resetOtpFailed: 'ResetOtpPage.error',
    resetOtpNotification: 'ResetOtpPage.resetOtpNotification',
    loadedPendingActions: 'LoginPage.loadedPendingActions',
    loadedSelf: 'LoginPage.loadedSelf',
};

export const ActionCreators = {
    getMyself() {
        return (dispatch) => {
            return getMyself()
                .then((res) => {
                    dispatch({
                        type: ActionTypes.loadedSelf,
                        payload: res.manager,
                    });
                    dispatch(routerHelperActions.makeRedirect('/'));
                })
                .catch(() => {
                    dispatch(authLogout());
                });
        };
    },
    login(
        email: string,
        password: string,
        resetOtp: boolean,
        otp?: string,
        secret?: string,
        searchParamsHook?: SearchParamsHook
    ) {
        return async (dispatch) => {
            try {
                dispatch(closeNotificationAction);
                const response = await login(email, password, resetOtp, otp, secret);
                dispatch({
                    type: SecurityActionTypes.updateOtpData,
                    payload: response,
                });

                if (response.apiToken) {
                    windowStorage.setItem(WindowStorageToken.managerApiToken, response.apiToken);
                    dispatch({
                        type: ActionTypes.loginSuccessful,
                        payload: {
                            apiToken: windowStorage.getItem(WindowStorageToken.managerApiToken),
                            manager: response.user,
                        },
                    });
                    dispatch({
                        type: SecurityActionTypes.updateOtpData,
                        payload: response,
                    });
                    dispatch(resetSessionProlongAlertState);
                    dispatch(ActionCreators.getPendingActions());

                    if (searchParamsHook.redirectUrl && !resetOtp) {
                        dispatch(routerHelperActions.makeRedirect(searchParamsHook.redirectUrl));
                    } else {
                        dispatch(routerHelperActions.makeRedirect('/home'));
                    }
                    dispatch(AppConfigActionCreators.loadApplicationConfig());
                } else if (response.status === 204) {
                    if (resetOtp) {
                        dispatch(successAction('OTP has been changed successfully'));
                        dispatch({
                            type: ActionTypes.resetOtpSuccessful,
                            payload: true,
                        });
                        setTimeout(function () {
                            window.location.replace(window.location.origin);
                        }, 2000);
                    } else {
                        dispatch({
                            type: ActionTypes.resetOtpNotification,
                            payload: true,
                        });
                    }
                } else if (resetOtp) {
                    dispatch({
                        type: SecurityActionTypes.updateOtpData,
                        payload: response,
                    });
                }
            } catch (error) {
                if (!!error.body) {
                    try {
                        error.bodyParsed = await error.json();
                    } catch (e) {}
                }
                if (resetOtp) {
                    if (error.status === 410) {
                        dispatch(errorAction('MFA set link expired'));
                    } else if (error.status === 404) {
                        dispatch(errorAction('Invalid manager id in token!'));
                    } else if (error.status === 400) {
                        dispatch(errorAction('Invalid token!'));
                    }
                }
                dispatch({
                    type: ActionTypes.loginFailed,
                    payload: { error },
                });
            }
        };
    },

    checkToken() {
        return (dispatch: Dispatch<AnyAction>) => {
            dispatch({ type: ActionTypes.resetOtpRequested });
            return resetPassword('').catch(async (error) => {
                if (error.status === 404) {
                    dispatch({
                        type: ActionTypes.resetOtpFailed,
                        payload: { error: 'invalidToken' },
                    });
                }
                if (error.status === 410) {
                    dispatch({
                        type: ActionTypes.resetOtpFailed,
                        payload: { error: 'expired' },
                    });
                }
            });
        };
    },

    refreshToken() {
        return async (dispatch) => {
            return authRefresh()
                .then((response) => {
                    if (!response.access_token) {
                        dispatch(errorAction('Your session has expired.'));
                        dispatch(ActionCreators.logout());
                        return;
                    }
                    windowStorage.setItem(WindowStorageToken.managerApiToken, response.access_token);
                    dispatch({
                        type: SessionProlongActionTypes.newToken,
                        payload: {
                            apiToken: response.access_token,
                        },
                    });
                })
                .catch(() => {
                    dispatch(errorAction('Your session has expired.'));
                    dispatch(ActionCreators.logout());
                });
        };
    },

    sendOtpCode(email: string, password: string) {
        return async (dispatch: Dispatch<AnyAction>) => {
            try {
                await sendOtpCode(email, password);
                dispatch(successAction('Email with OTP code sent.'));
            } catch (err) {
                dispatch(errorAction('Failed to sent OTP code.'));
            }
        };
    },

    prolongSession() {
        return async (dispatch) => {
            return newAuthToken().then((response) => {
                windowStorage.setItem(WindowStorageToken.managerApiToken, response.apiToken);
                dispatch({
                    type: SessionProlongActionTypes.newToken,
                    payload: {
                        apiToken: windowStorage.getItem(WindowStorageToken.managerApiToken),
                    },
                });
                dispatch(resetSessionProlongAlertState);
                dispatch(successAction('The session has been successfully prolonged.'));
            });
        };
    },

    logout() {
        return async (dispatch: Dispatch<AnyAction>) => {
            try {
                const res = await authLogout();
                if (res.logout_url) {
                    windowStorage.removeItem(WindowStorageToken.managerApiToken);
                    window.location.replace(res.logout_url);
                }
            } catch (e) {
                dispatch(errorAction('Logout failed'));
            }
        };
    },
    reloadUserData() {
        return (dispatch, getState: () => State) => {
            const manager = getState().auth.manager;
            if (manager) {
                return getManager(manager.id).then((manager) => {
                    dispatch({
                        type: ActionTypes.reloadUserSuccess,
                        payload: { manager },
                    });
                    dispatch(ActionCreators.getPendingActions());
                });
            }
        };
    },
    requestPasswordReset() {
        return async () => {
            const res = await requestPasswordResetApi();
            if (res && res.resetLink) {
                window.location.replace(res.resetLink);
            }
        };
    },
    getPendingActions() {
        return (dispatch, getState: () => State) =>
            getPendingActions().then((pendingActions) => {
                if (getState().auth.manager) {
                    const permissions = getState().auth.manager.profile?.permissions;
                    dispatch({
                        type: ActionTypes.loadedPendingActions,
                        payload: Object.assign({}, { pendingActions }, { permissions }),
                    });
                }
            });
    },
    endSession() {
        return (dispatch) => {
            dispatch(closeSessionProlongAlert);
            dispatch(ActionCreators.logout());
        };
    },
};

export function authReducer(state: AuthState = new AuthState(), action: Action) {
    switch (action.type) {
        case ActionTypes.loginSuccessful: {
            const loginPayload = action.payload as { apiToken: string; manager: Manager };
            return new AuthState(loginPayload.apiToken, loginPayload.manager);
        }
        case ActionTypes.loginFailed: {
            const failedPayload = action.payload as { error: unknown };
            return new AuthState(null, null, failedPayload.error);
        }
        case ActionTypes.logoutSuccessful:
            return new AuthState();
        case SessionProlongActionTypes.newToken: {
            const tokenPayload = action.payload as { apiToken: string };
            return new AuthState(tokenPayload.apiToken, state.manager);
        }
        case ActionTypes.selfEdit: {
            const selfEditPayload = action.payload as { manager: Manager };
            return new AuthState(state.apiToken, selfEditPayload.manager);
        }
        case ActionTypes.reloadUserSuccess: {
            const reloadPayload = action.payload as { manager: Manager };
            return new AuthState(state.apiToken, reloadPayload.manager);
        }
        case ActionTypes.resetOtpRequested:
            return new AuthState();
        case ActionTypes.resetOtpFailed:
            return new AuthState();
        case ActionTypes.resetOtpNotification:
            return new AuthState(null, null, null, action.payload as boolean);
        case ActionTypes.resetOtpSuccessful:
            return new AuthState(null, null, null, null, true);
        case ActionTypes.loadedSelf:
            return new AuthState(state.apiToken, action.payload as Manager);
    }

    return state;
}
